import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import Cookies from "js-cookie";
import "../../../styles/org-buttons.css";
import { buttonText } from "../../../utils/buttonText";
import { pageText } from "../../../utils/pageText";

function ReassignDatasetPanel({ organizationNames }) {
  const [datasets, setDatasets] = useState([]);
  const [selectedNames, setSelectedNames] = useState([]);
  const [targetOrg, setTargetOrg] = useState("");
  const [filterOrg, setFilterOrg] = useState("all");
  const [filterType, setFilterType] = useState("all");
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [search, setSearch] = useState("");

  const token = Cookies.get("user_token");

  const orgOptions = useMemo(() => {
    const names = new Set(organizationNames || []);
    names.add("public");
    return Array.from(names).sort();
  }, [organizationNames]);

  const fetchDatasets = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const response = await axios.post("/datasets/list", {
        requesterToken: token,
      });
      setDatasets(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      window.alert(error.response?.data || "Failed to load datasets");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDatasets();
  }, []);

  const filtered = useMemo(() => {
    return datasets.filter((ds) => {
      const org = ds.organization || "public";
      const media = ds.media_type || ds.folder_type || "image";
      if (filterOrg !== "all" && org !== filterOrg) return false;
      if (filterType !== "all" && media !== filterType) return false;
      if (search && !String(ds.name || "").toLowerCase().includes(search.toLowerCase())) {
        return false;
      }
      return true;
    });
  }, [datasets, filterOrg, filterType, search]);

  const toggleName = (name) => {
    setSelectedNames((prev) =>
      prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name]
    );
  };

  const selectAllFiltered = () => {
    setSelectedNames(filtered.map((d) => d.name));
  };

  const clearSelection = () => setSelectedNames([]);

  const runReassign = async (dryRun) => {
    if (!selectedNames.length) {
      window.alert("Select at least one dataset");
      return;
    }
    if (!targetOrg) {
      window.alert("Select target organization");
      return;
    }
    if (
      !dryRun &&
      !window.confirm(
        `Move ${selectedNames.length} dataset(s) to organization "${targetOrg}"?\n` +
          "This moves disk paths and updates FiftyOne. CVAT/Label Studio tasks may need re-import."
      )
    ) {
      return;
    }
    setSubmitting(true);
    setResult(null);
    try {
      const response = await axios.post("/datasets/reassign_organization", {
        requesterToken: token,
        dataset_names: selectedNames,
        target_organization: targetOrg,
        dry_run: dryRun,
      });
      setResult(response.data);
      if (!dryRun) {
        await fetchDatasets();
        clearSelection();
      }
    } catch (error) {
      const msg =
        error.response?.data?.error ||
        error.response?.data ||
        error.message ||
        "Reassign failed";
      window.alert(typeof msg === "string" ? msg : JSON.stringify(msg));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="reassign-org-panel">
      <div className="reassign-org-hero">
        <h3 className="reassign-org-title">{pageText("Reassign Dataset Organization")}</h3>
        <p className="reassign-org-desc">
          {pageText("Admin only. Moves media / info / ETL folders and updates FiftyOne organization + sample filepaths.")}
        </p>
      </div>

      <div className="reassign-org-toolbar">
        <label className="reassign-org-field">
          <span>{pageText("Filter org")}</span>
          <select
            value={filterOrg}
            onChange={(e) => setFilterOrg(e.target.value)}
          >
            <option value="all">{pageText("All")}</option>
            {orgOptions.map((org) => (
              <option key={org} value={org}>
                {org}
              </option>
            ))}
          </select>
        </label>
        <label className="reassign-org-field">
          <span>{pageText("Media type")}</span>
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
          >
            <option value="all">{pageText("All")}</option>
            <option value="image">{pageText("image")}</option>
            <option value="video">{pageText("video")}</option>
            <option value="audio">{pageText("audio")}</option>
            <option value="text">{pageText("text")}</option>
            <option value="table">{pageText("table")}</option>
          </select>
        </label>
        <label className="reassign-org-field reassign-org-search">
          <span>{pageText("Search")}</span>
          <input
            type="text"
            value={search}
            placeholder={pageText("dataset name")}
            onChange={(e) => setSearch(e.target.value)}
          />
        </label>
        <label className="reassign-org-field">
          <span>{pageText("Target organization")}</span>
          <select
            value={targetOrg}
            onChange={(e) => setTargetOrg(e.target.value)}
          >
            <option value="">{pageText("Select...")}</option>
            {orgOptions.map((org) => (
              <option key={org} value={org}>
                {org}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="reassign-org-actions">
        <button
          type="button"
          className="org-btn org-btn-secondary"
          onClick={selectAllFiltered}
          disabled={loading || !filtered.length}
        >
          {buttonText(`Select filtered (${filtered.length})`)}
        </button>
        <button
          type="button"
          className="org-btn org-btn-secondary"
          onClick={clearSelection}
          disabled={!selectedNames.length}
        >
          {buttonText("Clear")}
        </button>
        <button
          type="button"
          className="org-btn org-btn-secondary"
          onClick={() => runReassign(true)}
          disabled={submitting || !selectedNames.length || !targetOrg}
        >
          {buttonText("Dry run")}
        </button>
        <button
          type="button"
          className="org-btn org-btn-primary"
          onClick={() => runReassign(false)}
          disabled={submitting || !selectedNames.length || !targetOrg}
        >
          {buttonText(submitting ? "Working…" : `Move selected (${selectedNames.length})`)}
        </button>
        <button
          type="button"
          className="org-btn org-btn-secondary"
          onClick={fetchDatasets}
          disabled={loading}
        >
          {buttonText("Refresh")}
        </button>
      </div>

      <div className="reassign-org-table-wrap">
        {loading ? (
          <div className="reassign-org-empty">{pageText("Loading datasets...")}</div>
        ) : filtered.length === 0 ? (
          <div className="reassign-org-empty">{pageText("No datasets match filters")}</div>
        ) : (
          <table className="reassign-org-table">
            <thead>
              <tr>
                <th />
                <th>{pageText("Name")}</th>
                <th>{pageText("Type")}</th>
                <th>{pageText("Organization")}</th>
                <th>{pageText("Samples")}</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((ds) => {
                const media = ds.media_type || ds.folder_type || "image";
                const org = ds.organization || "public";
                const samples =
                  ds.total_samples ??
                  ds.samples?.length ??
                  (Array.isArray(ds.samples) ? ds.samples.length : 0);
                return (
                  <tr key={ds.name}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedNames.includes(ds.name)}
                        onChange={() => toggleName(ds.name)}
                      />
                    </td>
                    <td>{ds.name}</td>
                    <td>{media}</td>
                    <td>{org}</td>
                    <td>{samples}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {result && (
        <pre className="reassign-org-result">
          {JSON.stringify(result, null, 2)}
        </pre>
      )}
    </div>
  );
}

export default ReassignDatasetPanel;
